import axios from "axios";
import { FileWithPreview } from "@contexts/index";
import { compressForUpload } from "src/utils/compress-image";
import { pickUploadSource } from "./file-snapshot";
import { createMetadataFlusher } from "./metadata-flusher";
import { createMetadataTransport } from "./metadata-transport";
import { photosApi } from "src/services/api";
import {
  createPresignQueue,
  PRESIGN_TIMEOUT_MS,
  type PresignedEntry,
} from "./presign-queue";
import { inAppBrowserTag } from "./in-app-tag";
import {
  isPageHidden,
  sharedHiddenTime,
  whenVisible,
  type HiddenTimeTracker,
} from "./page-visibility";
import * as Sentry from "@sentry/nextjs";
import { NestJSError } from "../types";

// Timeout policy lives in its own module: it is pure arithmetic calibrated
// against production latency, and keeping it free of axios/Sentry/context
// imports is what makes it testable. Re-exported so existing callers are
// unaffected.
export { uploadTimeoutMs, uploadTotalBudgetMs } from "./upload-timeout";
import { uploadTimeoutMs, uploadTotalBudgetMs } from "./upload-timeout";
import {
  createUploadGate,
  isBackendWideFailure,
  type UploadGate,
} from "./upload-gate";

/** Flattens an upload failure into something Sentry can actually be read from. */
export function describeUploadError(error: unknown): Record<string, unknown> {
  if (axios.isAxiosError(error)) {
    return {
      kind: "axios",
      // ECONNABORTED = our timeout, ERR_NETWORK = the request never completed.
      code: error.code ?? null,
      message: error.message,
      // 0/undefined means no HTTP response at all: offline, tab suspended,
      // rejected CORS preflight, or a killed renderer.
      status: error.response?.status ?? 0,
    };
  }
  if (error instanceof Error) {
    return { kind: error.name, message: error.message };
  }
  return { kind: "unknown", message: String(error) };
}

/**
 * Can we still read the bytes of this File?
 *
 * A `status 0` PUT says only "the request never completed". That covers the
 * network being gone AND iOS having reclaimed the picked file handle behind our
 * back (the same cause as the NotFoundError seen during thumbnailing). Reading
 * a single byte back separates the two, which is the whole difference between
 * "the customer's connection dropped" and "the file is gone, re-picking is the
 * only fix". Never throws.
 */
async function isFileStillReadable(file: File): Promise<boolean> {
  try {
    await file.slice(0, 1).arrayBuffer();
    return true;
  } catch {
    return false;
  }
}

/**
 * Times one file may be resumed after failing while the page was hidden. A
 * customer who leaves the app twice mid-upload still gets their photos; a file
 * that fails for some other reason every time still ends up reported.
 */
const MAX_HIDDEN_RESUMES = 2;

/** Attempts every file gets before the time budget may stop it. */
const MIN_ATTEMPTS = 2;

/**
 * Uploads a file with retry mechanism.
 *
 * The SAME presigned URL is reused on every attempt, deliberately. Re-fetching
 * a fresh URL per retry hands back a NEW object key, and any mismatch between
 * the key we PUT to and the key we register metadata against orphans the photo
 * (bytes in storage, DB pointing elsewhere). Presigned URLs are valid for 12h,
 * far longer than any retry window, so there is nothing to refresh anyway.
 */
export async function uploadFileWithRetry(
  url: string,
  headUrl: string,
  file: File,
  retries = 6,
  /**
   * Shared across one batch. Without it each file rides out a backend outage
   * alone, exhausts its attempts in ~40s and is lost while the backend is
   * still coming back. Optional so single-file callers (greeting card, admin
   * media) are unaffected.
   */
  gate?: UploadGate,
  /**
   * Where hidden-page time comes from. Injectable for tests; the page-wide
   * tracker otherwise.
   */
  hiddenTime: HiddenTimeTracker = sharedHiddenTime()
): Promise<void> {
  const timeout = uploadTimeoutMs(file.size);
  // Bound the TOTAL effort per file, not just each attempt.
  //
  // Raising the per-attempt timeout to cover the p99 would otherwise multiply
  // straight through the retry loop: six attempts at 60s plus backoff is over
  // six minutes staring at a spinner for one dead file, worse than the problem
  // being fixed. The deadline lets a slow-but-progressing upload have its full
  // timeout while a file that is genuinely going nowhere gives up in about the
  // time it used to.
  const budget = uploadTotalBudgetMs(file.size);
  const startedAt = Date.now();
  const gateStallAtStart = gate?.stalledMs() ?? 0;
  const hiddenAtStart = hiddenTime.hiddenMs();
  // Time parked at the gate is the backend being down, not this file being
  // slow. Counting it would expire the budget during exactly the outage the
  // gate exists to survive, and lose the photo anyway.
  //
  // Time with the page hidden is excluded for the same reason: iOS and in-app
  // WebViews freeze a backgrounded tab, so a customer who switched apps for
  // five minutes came back to files that had "used up" their budget after a
  // single attempt (FOTO-NEXTJS-14N). Only active time is the file's own.
  const elapsedExcludingStall = () =>
    Date.now() -
    startedAt -
    ((gate?.stalledMs() ?? 0) - gateStallAtStart) -
    (hiddenTime.hiddenMs() - hiddenAtStart);

  let attempt = 0;
  for (;;) {
    attempt += 1;
    // Never fewer than MIN_ATTEMPTS: whatever the clock says, one attempt is
    // not a fair chance -- the first may have been cut off by a suspension
    // the visibility events never reported.
    if (attempt > MIN_ATTEMPTS && elapsedExcludingStall() >= budget) {
      throw new Error(
        `Upload budget exhausted for ${file.name} after ${attempt - 1} attempts`
      );
    }
    // Wait behind the gate ONLY while it is still willing to wait. Once its
    // budget is spent the gate steps aside entirely and this file takes its
    // ordinary six attempts -- exactly the behaviour that existed before the
    // gate did.
    //
    // It used to throw here instead. That made the gate strictly worse than
    // its own absence: on 2026-09-07 a customer lost 289 of 340 photos to
    // "storage unavailable for too long" without a single upload being
    // attempted, where the old per-file retries would have carried most of
    // them. A gate may add patience. It must never remove attempts.
    if (gate && !gate.isExhausted()) {
      await gate.beforeAttempt();
    }
    try {
      await axios.put(url, file, {
        headers: {
          "Content-Type": file.type,
        },
        timeout,
      });

      // Verify the upload. The FATAL guard is the size check (HEAD
      // content-length vs file.size) — it catches the only corruption that
      // actually happens in the wild: iOS / in-app-browser truncation, where
      // bytes go missing. The MD5/ETag comparison is ADVISORY ONLY: a file
      // whose bytes actually landed (correct size) must never be dropped just
      // because a re-read of the File hashed differently. On iOS Safari and
      // Facebook/Instagram in-app browsers a File can return shifted bytes on a
      // later read, so an ETag mismatch is almost always a client re-read
      // artifact, not a bad upload (TLS already guarantees transit integrity).
      // Throwing on it silently dropped photos that were sitting fine in MinIO
      // — the main cause of "everything uploads but some photos vanish".

      // Fatal guard: true truncation. Only throw on a real, known size that
      // differs — never on a missing/unreadable content-length.
      const headResponse = await axios.head(headUrl, {
        headers: { "Content-Type": file.type },
        timeout: 30000,
      });
      const remoteFileSize = parseInt(
        String(headResponse.headers["content-length"]),
        10
      );
      if (Number.isFinite(remoteFileSize) && remoteFileSize !== file.size) {
        console.error(
          `Size mismatch: expected ${file.size} bytes but got ${remoteFileSize} bytes`
        );
        throw new Error("Uploaded file size does not match original size");
      }

      // There used to be an advisory full-file MD5 here, compared with the
      // returned ETag. It could never drop a photo (a mismatch only logged),
      // yet it re-read every file end to end after its PUT: a second full read
      // per photo on exactly the phones and in-app WebViews whose handles die
      // under memory pressure, and real CPU on a 2,000-photo batch. The size
      // check above is the guard that matters; TLS covers transit.

      gate?.recordSuccess();
      return; // Upload succeeded and passed the size guard.
    } catch (error) {
      gate?.recordFailure(error);
      const detail = describeUploadError(error);
      // Breadcrumb every attempt: when a whole batch dies at once, the only way
      // to tell a rejected CORS preflight from an offline phone from a killed
      // renderer is to see the code/status of each attempt in the trail.
      Sentry.addBreadcrumb({
        category: "upload.put",
        level: "warning",
        message: `PUT failed for ${file.name} (attempt ${attempt}/${retries})`,
        data: { ...detail, sizeKB: Math.round(file.size / 1024), timeout },
      });

      // A backend-wide outage must not burn this file's attempts. The gate
      // bounds the total wait (and throws once exhausted), so retrying here is
      // safe and is the whole point: the measured stall lasted four minutes
      // while six attempts covered about forty seconds.
      if (gate && !gate.isExhausted() && isBackendWideFailure(error)) {
        attempt -= 1;
        continue;
      }

      if (attempt >= retries) {
        throw error;
      }
      // Exponential backoff, capped ~10s, with light jitter so concurrent
      // failed files don't all retry in lockstep.
      const base = Math.min(1000 * 2 ** (attempt - 1), 10000); // 1s,2s,4s,8s,10s
      const delay = Math.round(base * (0.85 + Math.random() * 0.3)); // ±15%
      console.warn(
        `Retrying upload for ${file.name}, attempt ${attempt + 1} (in ${delay}ms)`
      );
      await new Promise((res) => setTimeout(res, delay));
    }
  }
}

/**
 * Upload files with order photo ID
 */
export const startUploadWithOrderPhoto = async (
  files: FileWithPreview[],
  orderId: string,
  orderPhotoId: string,
  limit: import("p-limit").LimitFunction, // pLimit instance
  setUploading: (state: boolean) => void,
  setUploadProgress: (progress: number) => void,
  setUploadingBuffer: (buffer: number) => void,
  setUploadError: (error: NestJSError | null) => void,
  concurrentUploadLimit: number = 3,
  batchUploadSize: number = 3,
  /**
   * Translator from the calling component.
   *
   * These messages were hardcoded Slovenian, and they are the ones a customer
   * reads at the worst moment they will have with us — a Polish customer on
   * foto-chwila.pl who had just lost 58 of 96 photos was told so in Slovenian.
   * Optional so existing callers keep working; the English text is the
   * fallback, which is still better than the wrong language.
   */
  translate?: (
    key: string,
    fallbackOrOptions?: string | Record<string, unknown>,
    defaultMessageOrOptions?: string | Record<string, unknown>,
  ) => string,
  hooks: {
    /**
     * Called once per file, with its index, the moment its bytes are verified
     * in storage. From then on the server holds the photo (our metadata call,
     * or failing that the upload-intent sweep), so the caller must stop
     * treating it as a pick that still needs uploading.
     */
    onFileLanded?: (index: number) => void;
    /**
     * Called once per attempt, just before a file's PUT, with how to ask
     * storage whether it arrived: the presigned HEAD URL and the exact size
     * being sent. If the tab dies before onFileLanded, this is what lets the
     * reload tell a photo already in storage from one that still needs
     * uploading.
     */
    onFileSending?: (index: number, check: { headUrl: string; size: number }) => void;
    /**
     * Called when a chunk of files is presigned, with each file's index and
     * storage key. The server records an upload intent per key right then, so
     * this is the moment a reload must be able to name them (see
     * pending-picks.ts presigned()).
     */
    onFilesPresigned?: (entries: { index: number; key: string }[]) => void;
    /**
     * The rank of the first file. 0 for a new group; uploading into a group
     * that already holds photos (a resumed selection going back to the group
     * it was picked for) starts after its highest rank, so the new photos sort
     * after the ones already there instead of interleaving with them.
     */
    firstRank?: number;
  } = {},
): Promise<void> => {
  const t = (key: string, fallback: string, vars?: Record<string, unknown>) => {
    if (!translate) return fallback;
    try {
      const out = vars ? translate(key, vars, fallback) : translate(key, fallback);
      return typeof out === "string" && out && out !== key ? out : fallback;
    } catch {
      return fallback;
    }
  };
  if (!files.length) return;

  setUploading(true);
  setUploadProgress(0);
  setUploadingBuffer(0);
  setUploadError(null);

  const counts = files.map((file) => file.count || 1);
  const firstRank =
    Number.isInteger(hooks.firstRank) && (hooks.firstRank as number) > 0
      ? (hooks.firstRank as number)
      : 0;
  const ranks = files.map((_, index) => firstRank + index);
  const totalFiles = files.length;

  // Declared here, not inside the try block below, so the `finally` --
  // which is a sibling scope of `try`, not nested inside it -- can still see
  // it to remove the listeners on every exit path.
  let onHide: ((event?: Event) => void) | undefined;
  // Files whose bytes are verified in storage (PUT plus size check, or the
  // final HEAD below). Attached to anything thrown out of here, so the caller
  // never deletes a group whose photos the server is about to register.
  let landedCount = 0;

  try {
    // Tell the server what is coming, exactly as the app does. With
    // orderPhotosId the backend writes an upload_intent row per file, and its
    // sweep registers anything that landed in storage even if this tab dies
    // before our own metadata call goes out. The sweep inserts against the
    // same (group, rank, key) uniqueness as uploadMetadataBatch, so when we DO
    // register first its insert is a no-op -- which only holds if the rank and
    // count here are the very ones the flusher sends below.
    const fileInfos = files.map((file, index) => ({
      name: file.file.name,
      type: file.file.type,
      count: counts[index],
      rank: ranks[index],
    }));

    // Requested chunk by chunk as the PUT queue reaches them, with retry --
    // see presign-queue.ts. A presign failure now costs only the files of
    // that chunk, never the whole batch before a byte has moved.
    const presign = createPresignQueue({
      files: fileInfos,
      request: async (chunk) => {
        const response = await photosApi.photoControllerGetPresignedUrls(
          { orderPhotosId: orderPhotoId, files: chunk },
          // Per request: the shared API instance has no timeout, and a
          // presign that never answers would stall its chunk for good.
          { timeout: PRESIGN_TIMEOUT_MS },
        );
        return response.data.urls;
      },
      onPresigned: (entries) => hooks.onFilesPresigned?.(entries),
      // No point retrying into a hidden page's dead network.
      beforeRetry: () => whenVisible(),
      onRetry: (error, attempt, chunk) => {
        Sentry.addBreadcrumb({
          category: "upload.presign",
          level: "warning",
          message: `Presign chunk ${chunk} failed (attempt ${attempt}), retrying`,
          data: describeUploadError(error),
        });
      },
    });

    setUploadingBuffer(100);

    // Upload files to S3/Minio using presigned URLs. Each file's outcome is
    // captured (settled) instead of throwing out of the batch, so one flaky
    // file cannot reject the whole Promise.all and void the entire order.
    let completed = 0;
    // Register photos as they land rather than once at the end, so a tab killed
    // mid-batch keeps everything already uploaded. See metadata-flusher.ts.
    // send/onError/beacon live in metadata-transport.ts so they can be tested
    // directly -- they carry the auth, requeue and error-shape behaviour that
    // decides whether a photo is registered, and nothing could reach them here.
    const flusher = createMetadataFlusher(
      createMetadataTransport({ orderId, orderPhotoId })
    );

    // Registered right after the flusher so a hidden/closed tab can still get
    // its last partial chunk out via fetch(keepalive) -- an in-flight XHR is
    // cancelled the moment the tab backgrounds or closes, but keepalive
    // survives it. Removed in the finally below so this can never outlive
    // the upload.
    //
    // The two events need different gating, not a shared one. `pagehide`
    // firing is ITSELF sufficient evidence the page is going away -- some
    // browsers (older Safari, iOS in-app WebViews, bfcache eviction) fire it
    // without `visibilityState` ever transitioning to "hidden", sometimes
    // with no preceding `visibilitychange` at all. Gating pagehide on
    // visibilityState silently disables the fallback in exactly the
    // in-app-browser case this feature exists for (see metadata-flusher.ts).
    // `visibilitychange`, by contrast, fires on every tab/app switch, so IT
    // still needs the "hidden" check to avoid flushing on a mere focus
    // change. One handler, branching on event.type, so the same function
    // object can be removed from both listeners in the finally below.
    onHide = (event?: Event) => {
      const isPageHide = event?.type === "pagehide";
      const isHiddenViaVisibilityChange =
        typeof document !== "undefined" && document.visibilityState === "hidden";
      if (isPageHide || isHiddenViaVisibilityChange) {
        flusher.flushOnHide();
      }
    };
    if (typeof window !== "undefined") {
      document.addEventListener("visibilitychange", onHide);
      window.addEventListener("pagehide", onHide);
    }

    // One gate for the whole batch: the first few backend-wide failures pause
    // every file at once instead of 1,400 of them independently hammering a
    // backend that is already refusing.
    const gate = createUploadGate();
    const hiddenTime = sharedHiddenTime();

    // One place for "this photo is in storage": tell the caller, count it,
    // and queue its registration. Used by a normal landing and by the final
    // storage check below.
    const markLanded = (index: number, key: string) => {
      landedCount += 1;
      try {
        hooks.onFileLanded?.(index);
      } catch {
        // A caller's bookkeeping must never fail an upload that landed.
      }
      // Queue immediately -- this does not block the next file.
      flusher.add({
        key,
        type: files[index].file.type,
        name: files[index].file.name,
        count: counts[index],
        rank: ranks[index],
        orderId: orderId,
        orderPhotosId: orderPhotoId,
      });
    };

    const results = await Promise.all(
      files.map((file, index) =>
        limit(async () => {
          let resumes = 0;
          // Kept outside the try so a failure can still be checked in storage.
          let entry: PresignedEntry | undefined;
          // The size of what was actually PUT (after compression), for the
          // final storage check.
          let sentSize: number | undefined;
          for (;;) {
            const hiddenBefore = hiddenTime.hiddenMs();
            try {
              entry = await presign.get(index);
              const { url, headUrl, key } = entry;
              if (!url || !headUrl) throw new Error("Presigned URL is missing");
              // Shrink large JPEG/PNG right before THIS file's PUT, so the work
              // overlaps the other files' network time instead of stalling the
              // batch. Always returns a usable File (the original if skipped or
              // failed) and never changes name/type, so the already-signed URL
              // and the metadata below stay valid.
              // Prefer the add-time byte snapshot (survives an in-app WebView
              // reclaiming the picked handle); fall back to the live handle.
              const source = pickUploadSource(file);
              const toUpload = await compressForUpload(source);
              sentSize = toUpload.size;
              try {
                hooks.onFileSending?.(index, { headUrl, size: sentSize });
              } catch {
                // Bookkeeping only; it must never stop the upload.
              }
              await uploadFileWithRetry(url, headUrl, toUpload, 6, gate, hiddenTime);
              completed += 1;
              setUploadProgress((completed / totalFiles) * 100);
              markLanded(index, key || "");
              return { index, ok: true as const, key: key || "" };
            } catch (err) {
              // Failed while the page was hidden: the customer left the app
              // (or locked the phone) and the WebView cut the network. That is
              // not this file's fault and its bytes are still here, so wait
              // until they are back and go again rather than reporting it lost.
              // Bounded, so a file that keeps failing still ends up reported.
              const failedWhileHidden =
                isPageHidden() || hiddenTime.hiddenMs() > hiddenBefore;
              if (failedWhileHidden && resumes < MAX_HIDDEN_RESUMES) {
                resumes += 1;
                await whenVisible();
                // A presign chunk that failed while hidden gets asked again.
                presign.reset();
                Sentry.addBreadcrumb({
                  category: "upload.resume",
                  level: "info",
                  message: `Resuming ${file.file.name} after the page was hidden (${resumes}/${MAX_HIDDEN_RESUMES})`,
                  data: describeUploadError(err),
                });
                continue;
              }
              completed += 1;
              setUploadProgress((completed / totalFiles) * 100);
              return {
                index,
                ok: false as const,
                name: file.file.name,
                error: err,
                entry,
                sentSize,
              };
            }
          }
        })
      )
    );
    const firstFailures = results.filter((r) => !r.ok);

    // Last look in storage for every file we gave up on. Six failed attempts
    // do not mean the bytes are missing: a PUT whose response (or whose size
    // HEAD) never came back -- dropped connection, suspended tab -- may well
    // have landed. Its upload intent exists, so the server sweep would
    // register it a minute later anyway; calling it lost told the customer to
    // add it again, and they ended up with it twice. Same size check as the
    // normal path: only an object of exactly the right size counts.
    const lostChecks = await Promise.all(
      firstFailures.map((failure) =>
        limit(async () => {
          const { entry } = failure;
          if (!entry?.headUrl) return failure;
          try {
            const head = await axios.head(entry.headUrl, { timeout: 15000 });
            const size = parseInt(String(head.headers["content-length"]), 10);
            const expected =
              failure.sentSize ?? pickUploadSource(files[failure.index]).size;
            if (size === expected) {
              markLanded(failure.index, entry.key || "");
              Sentry.addBreadcrumb({
                category: "upload.recovered",
                level: "info",
                message: `${failure.name} was in storage after all`,
              });
              return null;
            }
          } catch {
            // Not there, or we cannot tell: it stays lost.
          }
          return failure;
        }),
      ),
    );
    const lost = lostChecks.filter(
      (f): f is (typeof firstFailures)[number] => f !== null,
    );

    // Everything still queued goes now. Photos already flushed mid-batch are
    // registered and are NOT re-sent -- and if a chunk is retried, the backend
    // upsert makes the repeat a no-op. It matches on
    // (order_photo_id, rank, key), so a genuine second copy of the same image
    // (same key, different rank) and a different photo that happens to land on
    // the same rank (different key) BOTH still register; only an actual replay
    // of identical rows is dropped.
    //
    // drain() reports how many photos it permanently gave up registering.
    const unregistered = await flusher.drain();

    // Those photos are NOT lost. Their bytes passed the size check and each
    // has an upload intent (presign carries orderPhotosId), so the server's
    // sweep registers them within a couple of minutes. Counting them as
    // failures told customers to add them again, which produced duplicates.
    // Still reported, at warning: a rising rate means uploadMetadataBatch is
    // failing, and a customer who pays inside that window is priced without
    // them.
    if (unregistered > 0) {
      Sentry.captureMessage("Photo registration left to the server sweep", {
        level: "warning",
        tags: { area: "photo-upload", inAppBrowser: inAppBrowserTag() },
        extra: { orderId, orderPhotoId, totalFiles, unregistered },
      });
    }

    if (lost.length > 0) {
      // Truly lost photos: not in storage. Report it (both to the customer and
      // to Sentry -- an explicit captureException is used so this is visible
      // even though the client's ignoreErrors filter drops generic "Network
      // Error"s).
      //
      // The report carries the per-file failure reason, not just names. When
      // EVERY file in a batch fails with `status 0` right after the presigned
      // POST succeeded, the cause is upstream of any one file: the PUT goes to
      // a different origin than the API, so a rejected CORS preflight, a
      // dropped connection, a suspended tab or a dead File handle all look
      // identical from here. `origin`, `online` and `firstFailedFileReadable`
      // are what tell those apart in the next occurrence.
      const firstFailedFileReadable = await isFileStillReadable(
        files[lost[0].index].file,
      );

      // Severity tracks the damage, not the mere fact of a failure. Losing an
      // entire batch means the customer has nothing and the cause is systemic
      // (dead handle, CORS, offline). Losing a handful out of hundreds is
      // ordinary mobile attrition and the retry already covered the rest --
      // reporting that at `error` puts a page-worthy alert next to a
      // 1-in-407 blip and trains everyone to ignore both. Anything at or above
      // a quarter of the batch still reads as error.
      const failureRatio = totalFiles > 0 ? lost.length / totalFiles : 1;
      const severity: Sentry.SeverityLevel =
        failureRatio >= 0.25 ? "error" : "warning";

      Sentry.captureException(
        new Error(
          `Partial photo upload: ${lost.length}/${totalFiles} lost ` +
            `(${firstFailures.length - lost.length} more failed but were found in storage)`
        ),
        {
          level: severity,
          tags: {
            area: "photo-upload",
            allFailed: String(lost.length === totalFiles),
            inAppBrowser: inAppBrowserTag(),
          },
          extra: {
            orderId,
            orderPhotoId,
            totalFiles,
            failedCount: lost.length,
            recoveredFromStorage: firstFailures.length - lost.length,
            // Landed, registration left to the server sweep. Not lost.
            unregisteredCount: unregistered,
            failedNames: lost.map((f) => f.name).slice(0, 50),
            failureReasons: lost
              .slice(0, 10)
              .map((f) => ({ name: f.name, ...describeUploadError(f.error) })),
            origin:
              typeof window !== "undefined" ? window.location.origin : null,
            online:
              typeof navigator !== "undefined" ? navigator.onLine : null,
            firstFailedFileReadable,
          },
        }
      );
    }

    if (landedCount === 0) {
      // Nothing reached storage -- surface a hard error so the caller doesn't
      // open an empty cart. (Photos that landed but are not registered yet do
      // not take this branch: the server registers them.)
      // Set the customer-facing message here as well: the catch below only
      // knows how to read an axios-shaped error, so it replaced this text with
      // "Unknown error occurred" -- the least useful thing to show someone whose
      // whole upload just failed.
      const message =
        typeof navigator !== "undefined" && !navigator.onLine
          ? t(
              "order.uploadOffline",
              "Your connection dropped. Check your internet connection and try again.",
            )
          : t(
              "order.uploadFailedNothingAdded",
              "Your photos could not be added to the cart. Nothing was saved — please try again.",
            );
      setUploadError({
        statusCode: 0,
        message,
        error: "UPLOAD_FAILED",
      });
      // Flagged so the caller's catch can show THIS message rather than
      // replacing it with a generic one. "Your connection dropped" tells the
      // customer what to do next; "something went wrong" does not.
      throw Object.assign(new Error(message), { customerFacing: true });
    }

    if (unregistered >= landedCount) {
      // Every photo that landed is still unregistered: the group holds nothing
      // yet, so returning normally would open an empty cart in silence and
      // let the caller treat the upload as attached. The bytes ARE in storage
      // and the server sweep registers them, so this is not "nothing was
      // saved" -- re-adding them is what would duplicate them. Say so, and
      // throw so the caller keeps the group (landedCount > 0) without
      // pretending the photos are in the cart.
      const message = t(
        "order.uploadRegistrationPending",
        "Your photos were uploaded, but we could not add them to the cart yet. They will appear in the cart within a few minutes — please do not add them again.",
      );
      setUploadError({
        statusCode: 0,
        message,
        error: "REGISTRATION_PENDING",
      });
      throw Object.assign(new Error(message), { customerFacing: true });
    }

    if (lost.length > 0) {
      // Partial success: the order DOES have the rest of the photos. Tell the
      // customer how many really did not make it, so they can re-add exactly
      // those -- but do NOT throw, so the cart still opens with the rest.
      setUploadError({
        statusCode: 0,
        message: t(
          "order.uploadFailedPartial",
          `${lost.length} of ${totalFiles} photos did not upload. The rest are saved — please add the missing ones again.`,
          { failed: lost.length, total: totalFiles },
        ),
        error: "PARTIAL_UPLOAD_FAILURE",
      });
    }
  } catch (error: unknown) {
    console.error("Upload error:", error);
    const e = error as {
      response?: { status?: number; data?: { message?: string; error?: string } };
    };
    // A customer-facing error already set its own message and code above.
    const customerFacing =
      (error as { customerFacing?: boolean } | null)?.customerFacing === true;
    if (!customerFacing) {
      setUploadError({
        statusCode: e.response?.status || 500,
        // Fall through to the thrown Error's own message before the generic
        // string: a network failure has no response body, and "Unknown error
        // occurred" tells the customer nothing about what to do next.
        message:
          e.response?.data?.message ||
          (error instanceof Error ? error.message : "") ||
          "Unknown error occurred",
        error: e.response?.data?.error || "Internal Server Error",
      });
    }
    Sentry.captureException(error);
    // How many photos are in storage despite the failure. The caller's
    // empty-group cleanup must not delete a group the server is about to fill.
    if (error && typeof error === "object") {
      Object.assign(error, { landedCount });
    }
    throw error;
  } finally {
    setUploading(false);
    if (onHide && typeof window !== "undefined") {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
    }
  }
};

/**
 * Helper function to get URL parameters
 */
export function getUrlParameter(name: string): string {
  if (typeof window === "undefined") return "";

  name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
  const regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
  const results = regex.exec(window.location.search);
  return results === null
    ? ""
    : decodeURIComponent(results[1].replace(/\+/g, " "));
}
